// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  Badge,
  SimpleGrid,
  Divider,
  Tabs,
  Progress,
  Loader,
  Center,
  ActionIcon,
  Tooltip,
  Table,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconEdit,
  IconTrash,
  IconPlus,
  IconCurrencyDollar,
  IconBriefcase,
  IconChartPie,
  IconHistory,
  IconReceipt,
  IconUsers,
  IconCalendar,
} from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { StatusBadge } from '../../components/StatusBadge';
import { CurrencyDisplay } from '../../components/CurrencyDisplay';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import {
  useJob,
  useUpdateJob,
  useDeleteJob,
  useJobCostEntries,
  useCreateJobCostEntry,
  useJobBudgetSummary,
} from '../../hooks/useJobCosting';

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const STATUS_COLOR_MAP: Record<string, string> = {
  active: 'green',
  on_hold: 'yellow',
  completed: 'blue',
  cancelled: 'gray',
};

const COST_TYPE_OPTIONS = [
  { value: 'LABOR', label: 'Labor' },
  { value: 'MATERIAL', label: 'Material' },
  { value: 'EQUIPMENT', label: 'Equipment' },
  { value: 'OVERHEAD', label: 'Overhead' },
  { value: 'OTHER', label: 'Other' },
];

const COST_TYPE_COLORS: Record<string, string> = {
  LABOR: 'blue',
  MATERIAL: 'green',
  EQUIPMENT: 'orange',
  OVERHEAD: 'grape',
  OTHER: 'gray',
};

interface CostEntryRow extends Record<string, unknown> {
  id: string;
  jobId: string;
  jobName: string;
  employeeId: string;
  employeeName: string;
  date: string;
  hours: number;
  laborCost: number;
  costType: string;
  description?: string;
  payrollRunId?: string;
  createdAt: string;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function BudgetBreakdownSection({
  summary,
}: {
  summary: {
    budgetAmount: number;
    totalLabor: number;
    totalMaterial: number;
    totalEquipment: number;
    totalOverhead: number;
    totalOther: number;
    totalCost: number;
    remainingBudget: number;
    percentUsed: number;
  };
}) {
  const categories = [
    { label: 'Labor', amount: summary.totalLabor, color: 'blue' },
    { label: 'Material', amount: summary.totalMaterial, color: 'green' },
    { label: 'Equipment', amount: summary.totalEquipment, color: 'orange' },
    { label: 'Overhead', amount: summary.totalOverhead, color: 'grape' },
    { label: 'Other', amount: summary.totalOther, color: 'gray' },
  ];

  const totalCost = summary.totalCost || 1;

  return (
    <Stack gap="md">
      <Text size="lg" fw={600}>
        Cost Breakdown by Category
      </Text>

      <Paper withBorder p="md">
        <Stack gap="sm">
          {categories.map((cat) => {
            const percent = totalCost > 0 ? Math.round((cat.amount / totalCost) * 100) : 0;
            return (
              <div key={cat.label}>
                <Group justify="space-between" mb={4}>
                  <Group gap="xs">
                    <Badge color={cat.color} variant="dot" size="sm">
                      {cat.label}
                    </Badge>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>
                      {formatCurrency(cat.amount)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      ({percent}%)
                    </Text>
                  </Group>
                </Group>
                <Progress value={percent} color={cat.color} size="sm" radius="xl" />
              </div>
            );
          })}
        </Stack>
      </Paper>

      <Paper withBorder p="md">
        <Text size="sm" fw={600} mb="sm">
          Budget Summary
        </Text>
        <Table>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td>
                <Text size="sm" c="dimmed">Total Budget</Text>
              </Table.Td>
              <Table.Td ta="right">
                <Text size="sm" fw={500}>{formatCurrency(summary.budgetAmount)}</Text>
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td>
                <Text size="sm" c="dimmed">Total Spent</Text>
              </Table.Td>
              <Table.Td ta="right">
                <Text size="sm" fw={500}>{formatCurrency(summary.totalCost)}</Text>
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td>
                <Text size="sm" c="dimmed">Remaining</Text>
              </Table.Td>
              <Table.Td ta="right">
                <Text
                  size="sm"
                  fw={600}
                  c={summary.remainingBudget < 0 ? 'red' : 'green'}
                >
                  {formatCurrency(summary.remainingBudget)}
                </Text>
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td>
                <Text size="sm" c="dimmed">% Used</Text>
              </Table.Td>
              <Table.Td ta="right">
                <Text
                  size="sm"
                  fw={600}
                  c={
                    summary.percentUsed >= 90
                      ? 'red'
                      : summary.percentUsed >= 75
                        ? 'orange'
                        : 'green'
                  }
                >
                  {summary.percentUsed}%
                </Text>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  );
}

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: job, loading: isLoading } = useJob(id);
  const { data: costEntriesDataRaw, isLoading: costEntriesLoading } = useJobCostEntries(id);
  const costEntriesData = costEntriesDataRaw as any;
  const { data: budgetSummaryRaw } = useJobBudgetSummary(id);
  const budgetSummary = budgetSummaryRaw as any;

  const updateMutation = useUpdateJob();
  const deleteMutation = useDeleteJob();
  const createEntryMutation = useCreateJobCostEntry();

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  // Delete modal
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  // Cost entry modal
  const [entryOpen, setEntryOpen] = useState(false);

  const editForm = useForm({
    initialValues: {
      name: '',
      code: '',
      description: '',
      clientName: '',
      budgetAmount: undefined as number | undefined,
      startDate: '',
      endDate: '',
      managerId: '',
      departmentId: '',
      status: 'ACTIVE',
    },
    validate: {
      name: (v: string) => (v.trim() ? null : 'Job name is required'),
      code: (v: string) => (v.trim() ? null : 'Job code is required'),
    },
  });

  const entryForm = useForm({
    initialValues: {
      employeeId: '',
      date: '',
      hours: 0 as number,
      laborCost: 0 as number,
      costType: 'LABOR',
      description: '',
    },
    validate: {
      employeeId: (v: string) => (v.trim() ? null : 'Employee is required'),
      date: (v: string) => (v ? null : 'Date is required'),
      hours: (v: number) => {
        if (v < 0) return 'Hours cannot be negative';
        return null;
      },
      laborCost: (v: number) => {
        if (v < 0) return 'Cost cannot be negative';
        return null;
      },
      costType: (v: string) => (v ? null : 'Cost type is required'),
    },
  });

  const openEdit = () => {
    if (!job) return;
    editForm.setValues({
      name: job.name,
      code: job.code,
      description: job.description || '',
      clientName: job.clientName || '',
      budgetAmount: job.budgetAmount ?? undefined,
      startDate: job.startDate || '',
      endDate: job.endDate || '',
      managerId: job.managerId || '',
      departmentId: job.departmentId || '',
      status: job.status,
    });
    setEditOpen(true);
  };

  const handleEdit = editForm.onSubmit((values) => {
    if (!id) return;
    const input: Record<string, unknown> = {
      name: values.name.trim(),
      code: values.code.trim(),
      status: values.status,
    };
    input.description = values.description.trim();
    input.clientName = values.clientName.trim();
    if (values.budgetAmount !== undefined && values.budgetAmount !== null) {
      input.budgetAmount = values.budgetAmount;
    }
    if (values.startDate) input.startDate = values.startDate;
    if (values.endDate) input.endDate = values.endDate;
    if (values.managerId.trim()) input.managerId = values.managerId.trim();
    if (values.departmentId.trim()) input.departmentId = values.departmentId.trim();

    updateMutation.update(id, input as any)
      .then(() => {
        notifications.show({
          title: 'Job Updated',
          message: 'A new version has been created (append-only).',
          color: 'green',
        });
        setEditOpen(false);
      })
      .catch((err: unknown) => {
        notifications.show({
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to update job',
          color: 'red',
        });
      });
  });

  const handleDelete = () => {
    if (!id || !deleteReason.trim()) return;
    deleteMutation.delete(id)
      .then(() => {
        notifications.show({
          title: 'Job Deactivated',
          message: 'Job soft-deleted (new inactive version created).',
          color: 'green',
        });
        setDeleteOpen(false);
        setDeleteReason('');
        navigate('/admin/workforce/jobs');
      })
      .catch((err: unknown) => {
        notifications.show({
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to delete job',
          color: 'red',
        });
      });
  };

  const handleCreateEntry = entryForm.onSubmit((values) => {
    if (!id) return;
    createEntryMutation.mutate(
      {
        jobId: id,
        employeeId: values.employeeId.trim(),
        date: values.date,
        hours: values.hours,
        laborCost: values.laborCost,
        costType: values.costType,
        description: values.description.trim() || undefined,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Cost Entry Added',
            message: 'The cost entry has been recorded.',
            color: 'green',
          });
          setEntryOpen(false);
          entryForm.reset();
        },
        onError: (err: any) => {
          notifications.show({
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to add cost entry',
            color: 'red',
          });
        },
      },
    );
  });

  const costEntryColumns: Column<CostEntryRow>[] = [
    {
      key: 'date',
      label: 'Date',
      sortable: true,
      width: 110,
      render: (row) => new Date(row.date).toLocaleDateString(),
    },
    {
      key: 'employeeName',
      label: 'Employee',
      sortable: true,
    },
    {
      key: 'hours',
      label: 'Hours',
      sortable: true,
      width: 80,
      render: (row) => (
        <Text size="sm">{row.hours.toFixed(1)}</Text>
      ),
    },
    {
      key: 'laborCost',
      label: 'Cost',
      sortable: true,
      width: 110,
      render: (row) => <CurrencyDisplay amount={row.laborCost} size="sm" />,
    },
    {
      key: 'costType',
      label: 'Type',
      width: 110,
      render: (row) => (
        <Badge
          color={COST_TYPE_COLORS[row.costType] || 'gray'}
          variant="light"
          size="sm"
        >
          {row.costType}
        </Badge>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      render: (row) => (
        <Text size="sm" lineClamp={1} c={row.description ? undefined : 'dimmed'}>
          {row.description || '-'}
        </Text>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!job) {
    return (
      <Stack gap="md">
        <PageHeader
          title="Job Not Found"
          breadcrumbs={[
            { label: 'Job Costing', to: '/admin/workforce/jobs' },
            { label: 'Not Found' },
          ]}
        />
        <EmptyState
          icon={<IconBriefcase size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
          title="Job Not Found"
          message="The requested job could not be found."
        />
      </Stack>
    );
  }

  const percentUsed = budgetSummary?.percentUsed ?? 0;
  const progressColor =
    percentUsed >= 90 ? 'red' : percentUsed >= 75 ? 'orange' : percentUsed >= 50 ? 'yellow' : 'green';

  return (
    <Stack gap="md">
      <PageHeader
        title={job.name}
        breadcrumbs={[
          { label: 'Job Costing', to: '/admin/workforce/jobs' },
          { label: job.name },
        ]}
        actions={
          <Group gap="sm">
            {job.version != null && (
              <Badge variant="light" color="gray" size="lg">
                v{job.version}
              </Badge>
            )}
            <StatusBadge status={job.status} colorMap={STATUS_COLOR_MAP} size="lg" />
            <Button
              icon={<IconEdit size={16} />}
              variant="light"
              onClick={openEdit}
            >
              Edit
            </Button>
            <Button
              icon={<IconTrash size={16} />}
              variant="light"
              color="red"
              onClick={() => setDeleteOpen(true)}
            >
              Deactivate
            </Button>
          </Group>
        }
      />

      {/* -- Summary Stats --------------------------------------------------- */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Budget"
          value={formatCurrency(budgetSummary?.budgetAmount ?? job.budgetAmount ?? 0)}
          icon={<IconCurrencyDollar size={24} />}
          color="blue"
        />
        <StatCard
          title="Total Cost"
          value={formatCurrency(budgetSummary?.totalCost ?? job.actualCost ?? 0)}
          icon={<IconReceipt size={24} />}
          color="orange"
        />
        <StatCard
          title="Remaining"
          value={formatCurrency(budgetSummary?.remainingBudget ?? ((job.budgetAmount ?? 0) - (job.actualCost ?? 0)))}
          icon={<IconCurrencyDollar size={24} />}
          color={
            (budgetSummary?.remainingBudget ?? ((job.budgetAmount ?? 0) - (job.actualCost ?? 0))) < 0
              ? 'red'
              : 'green'
          }
        />
        <Paper withBorder p="md" radius="md">
          <Group justify="space-between" ta="flex-start">
            <Stack gap={4} style={{ flex: 1 }}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                % Used
              </Text>
              <Text fw={700} fz={28} lh={1} c={progressColor}>
                {percentUsed}%
              </Text>
              <Progress
                value={Math.min(percentUsed, 100)}
                color={progressColor}
                size="sm"
                radius="xl"
                mt={8}
              />
            </Stack>
          </Group>
        </Paper>
      </SimpleGrid>

      {/* -- Job Details ----------------------------------------------------- */}
      <Paper p="lg" withBorder>
        <Text size="lg" fw={600} mb="md">
          Job Information
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Code</Text>
            <Text size="sm" fw={500} ff="monospace">{job.code}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Client</Text>
            <Text size="sm" fw={500}>{job.clientName || '-'}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Manager</Text>
            <Text size="sm" fw={500}>{job.managerName || '-'}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Department</Text>
            <Text size="sm" fw={500}>{job.departmentName || '-'}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Start Date</Text>
            <Text size="sm" fw={500}>
              {job.startDate ? new Date(job.startDate).toLocaleDateString() : '-'}
            </Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">End Date</Text>
            <Text size="sm" fw={500}>
              {job.endDate ? new Date(job.endDate).toLocaleDateString() : '-'}
            </Text>
          </Group>
          {job.createdAt && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Created</Text>
              <Text size="sm" fw={500}>{new Date(job.createdAt).toLocaleDateString()}</Text>
            </Group>
          )}
        </SimpleGrid>
        {job.description && (
          <>
            <Divider my="md" />
            <Text size="sm" fw={600} mb="xs">Description</Text>
            <Text size="sm" c="dimmed">{job.description}</Text>
          </>
        )}
      </Paper>

      {/* -- Tabs: Cost Entries / Budget Analysis / History ------------------- */}
      <Paper p="lg" withBorder>
        <Tabs defaultValue="entries">
          <Tabs.List>
            <Tabs.Tab value="entries" icon={<IconReceipt size={16} />}>
              Cost Entries
            </Tabs.Tab>
            <Tabs.Tab value="analysis" icon={<IconChartPie size={16} />}>
              Budget Analysis
            </Tabs.Tab>
            <Tabs.Tab value="history" icon={<IconHistory size={16} />}>
              History
            </Tabs.Tab>
          </Tabs.List>

          {/* -- Cost Entries Tab --------------------------------------------- */}
          <Tabs.Panel value="entries" pt="md">
            <Stack gap="md">
              <Group justify="flex-end">
                <Button
                  icon={<IconPlus size={16} />}
                  size="sm"
                  onClick={() => {
                    entryForm.reset();
                    setEntryOpen(true);
                  }}
                >
                  Add Cost Entry
                </Button>
              </Group>
              <DataTable<CostEntryRow>
                columns={costEntryColumns}
                data={(costEntriesData?.data as CostEntryRow[]) ?? []}
                loading={costEntriesLoading}
                getRowKey={(row) => row.id}
                emptyMessage="No cost entries recorded for this job yet."
                pagination={
                  costEntriesData
                    ? {
                        page: costEntriesData.page || 1,
                        pageSize: costEntriesData.perPage || 25,
                        total: costEntriesData.total,
                        onPageChange: () => {},
                      }
                    : undefined
                }
              />
            </Stack>
          </Tabs.Panel>

          {/* -- Budget Analysis Tab ----------------------------------------- */}
          <Tabs.Panel value="analysis" pt="md">
            {budgetSummary ? (
              <BudgetBreakdownSection summary={budgetSummary} />
            ) : (
              <Center py="xl">
                <Stack ta="center" gap="xs">
                  <IconChartPie size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />
                  <Text c="dimmed" size="sm">
                    No budget data available yet. Add cost entries to see analysis.
                  </Text>
                </Stack>
              </Center>
            )}
          </Tabs.Panel>

          {/* -- History Tab ------------------------------------------------- */}
          <Tabs.Panel value="history" pt="md">
            <Stack gap="sm">
              {job.version != null && job.version > 1 ? (
                <Paper p="md" withBorder>
                  <Group gap="sm" mb="sm">
                    <IconHistory size={18} color="var(--mantine-color-blue-6)" />
                    <Text size="sm" fw={600}>Version History</Text>
                  </Group>
                  <Text size="sm" c="dimmed">
                    This job is currently on version {job.version}. Previous versions are preserved
                    in the append-only audit trail for compliance and forensics.
                  </Text>
                  {job.originalId && (
                    <Text size="xs" c="dimmed" mt="xs">
                      Original ID: {job.originalId}
                    </Text>
                  )}
                </Paper>
              ) : (
                <Paper p="md" withBorder>
                  <Group gap="sm" mb="sm">
                    <IconHistory size={18} color="var(--mantine-color-dimmed)" />
                    <Text size="sm" fw={600}>Version History</Text>
                  </Group>
                  <Text size="sm" c="dimmed">
                    This is the first version of this job. Updates will create new immutable
                    versions.
                  </Text>
                </Paper>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Paper>

      {/* -- Edit Job Modal -------------------------------------------------- */}
      <Modal
        opened={editOpen}
        onClose={() => {
          setEditOpen(false);
          editForm.reset();
        }}
        title={`Edit Job: ${job.name}`}
        size="lg"
      >
        <form onSubmit={handleEdit}>
          <Stack gap="md">
            {job.version != null && (
              <Text size="xs" c="dimmed">
                Saving will create a new version (v{job.version + 1}). The previous version is
                preserved in the append-only audit log.
              </Text>
            )}

            <Group grow>
              <TextInput
                label="Job Name"
                required
                {...editForm.getInputProps('name')}
              />
              <TextInput
                label="Job Code"
                required
                {...editForm.getInputProps('code')}
              />
            </Group>

            <Textarea
              label="Description"
              minRows={2}
              {...editForm.getInputProps('description')}
            />

            <Group grow>
              <TextInput
                label="Client Name"
                {...editForm.getInputProps('clientName')}
              />
              <NumberInput
                label="Budget Amount"
                prefix="$"
                decimalScale={2}
                fixedDecimalScale
                min={0}
                thousandSeparator=","
                {...editForm.getInputProps('budgetAmount')}
              />
            </Group>

            <Group grow>
              <TextInput
                label="Start Date"
                type="date"
                {...editForm.getInputProps('startDate')}
              />
              <TextInput
                label="End Date"
                type="date"
                {...editForm.getInputProps('endDate')}
              />
            </Group>

            <Group grow>
              <TextInput
                label="Manager ID"
                {...editForm.getInputProps('managerId')}
              />
              <TextInput
                label="Department ID"
                {...editForm.getInputProps('departmentId')}
              />
            </Group>

            <Select
              label="Status"
              data={STATUS_OPTIONS}
              {...editForm.getInputProps('status')}
            />

            <Paper p="sm" bg="blue.0" radius="sm">
              <Text size="xs" c="blue.7">
                Append-only: Saving creates a new version. The previous version remains immutable
                in the audit trail.
              </Text>
            </Paper>

            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => {
                  setEditOpen(false);
                  editForm.reset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={updateMutation.loading}>
                Save New Version
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* -- Delete Confirmation Modal --------------------------------------- */}
      <Modal
        opened={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteReason('');
        }}
        title="Deactivate Job"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This will soft-delete{' '}
            <Text span fw={600}>
              {job.name}
            </Text>{' '}
            ({job.code}) by creating a new inactive version (append-only). The original record is
            preserved for audit.
          </Text>
          <Textarea
            label="Reason for deactivation"
            placeholder="Required for audit trail..."
            required
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.currentTarget.value)}
            minRows={2}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteOpen(false);
                setDeleteReason('');
              }}
            >
              Keep Job
            </Button>
            <Button
              color="red"
              disabled={!deleteReason.trim()}
              loading={deleteMutation.loading}
              onClick={handleDelete}
            >
              Deactivate Job
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* -- Add Cost Entry Modal -------------------------------------------- */}
      <Modal
        opened={entryOpen}
        onClose={() => {
          setEntryOpen(false);
          entryForm.reset();
        }}
        title="Add Cost Entry"
        size="lg"
      >
        <form onSubmit={handleCreateEntry}>
          <Stack gap="md">
            <TextInput
              label="Employee ID"
              placeholder="Enter employee ID"
              required
              {...entryForm.getInputProps('employeeId')}
            />

            <Group grow>
              <TextInput
                label="Date"
                type="date"
                required
                {...entryForm.getInputProps('date')}
              />
              <Select
                label="Cost Type"
                required
                data={COST_TYPE_OPTIONS}
                {...entryForm.getInputProps('costType')}
              />
            </Group>

            <Group grow>
              <NumberInput
                label="Hours"
                placeholder="0.0"
                decimalScale={1}
                min={0}
                max={24}
                step={0.5}
                {...entryForm.getInputProps('hours')}
              />
              <NumberInput
                label="Cost Amount"
                placeholder="0.00"
                prefix="$"
                decimalScale={2}
                fixedDecimalScale
                min={0}
                thousandSeparator=","
                {...entryForm.getInputProps('laborCost')}
              />
            </Group>

            <Textarea
              label="Description"
              placeholder="Optional description of this cost entry..."
              minRows={2}
              {...entryForm.getInputProps('description')}
            />

            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => {
                  setEntryOpen(false);
                  entryForm.reset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={createEntryMutation.isPending}>
                Add Entry
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
